import { useState, useEffect } from "react";
import { Button } from "../../components/ui/button";
import * as XLSX from 'xlsx';
import { useToast } from "../../hooks/use-toast";
import { ClaimValidationAxiosInstance } from "../../Network/ClaimValidation";


interface ValidationResult {
  success?: boolean;
  message?: string;
  error?: string;
  isParent?: boolean;
  errorType?: string;
  children?: Array<{
    message: string;
    isActivityError?: boolean;
    activityCode?: string;
    diagnosisCode?: string;
    relationType?: string;
    errorType?: string;
  }>;
  relation_Type?: string;
  error_type?: string;
  type?: string;
  activityCode?: string;
  code?: string;
  usedBenefit?: string;
  benefit?: string;  // أضف هذا
}

interface Validation {
  name: string;
  code?: string | string[];
  type?: string;
  usedBenefit?: string;
  benefit?: string;  // أضف هذا
  result?: ValidationResult | string;
}

interface ClaimResult {
  claim_ID?: string;
  patients?: string;
  patientGender?: string;
  age?: string;
  memberId?: string;
  patientPassport?: string;
  nationalIDNumber?: string;
  dateOfBirth?: string;
  Policy_id?: string;
  Policy_name?: string;
  Plan_name?: string;
  Provider_id?: string;
  Provider_name?: string;
  Network?: string;
  InsurancePlan?: string;
  payer?: string;
  vip?: string;
  doctor?: string;
  Primary_Diagnosis?: string;
  secondery_diagnosis_codes?: string;
  Activity_Code?: string;
  Activity_Type?: string;
  Quantity?: string;
  current_date?: string;
  relation_id?: string;
  Activity_Start?: string;
  Observation?: string;
  Encounter?: string;
  gross?: string;
  patientShare?: string;
  net?: string;
  type?: string;
  drugs?: string;
  specialty?: string;
  total?: string;
  date?: string;
  validations?: Validation[];
}

const BulkClaimValidation = () => {
  const { toast } = useToast();
  
  const token = localStorage.getItem("token") || "";

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [bulkValidationResults, setBulkValidationResults] = useState<ClaimResult[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [responseMessage, setResponseMessage] = useState("");
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [errorLogs, setErrorLogs] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'all' | 'passed' | 'failed'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');

  // Load results from localStorage on mount
  useEffect(() => {
    const savedResults = localStorage.getItem('bulkValidationResults');
    const savedMessage = localStorage.getItem('responseMessage');
    
    if (savedResults) {
      try {
        setBulkValidationResults(JSON.parse(savedResults));
        if (savedMessage) {
          setResponseMessage(savedMessage);
        }
      } catch (error) {
        console.error('Failed to load saved results:', error);
      }
    }
  }, []);

  const itemsPerPage = 5;

  const toggleRowExpansion = (rowKey: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowKey)) {
      newExpanded.delete(rowKey);
    } else {
      newExpanded.add(rowKey);
    }
    setExpandedRows(newExpanded);
  };

  const handleResetBulkUpload = () => {
    setUploadedFile(null);
    setBulkValidationResults([]);
    setUploadProgress(0);
    setResponseMessage("");
    setExpandedRows(new Set());
    setErrorLogs([]);
    setActiveTab('all');
    setCurrentPage(1);
    setSearchQuery('');
    
    // Clear localStorage
    localStorage.removeItem('bulkValidationResults');
    localStorage.removeItem('responseMessage');
  };

  const downloadTemplate = () => {
    const templateData = [
      {
        "claim_ID": "", "patients": "", "patientGender": "", "age": "",
        "memberId": "", "patientPassport": "", "nationalIDNumber": "",
        "dateOfBirth": "", "Policy_id": "", "Policy_name": "", "Plan_name": "",
        "Provider_id": "", "Provider_name": "", "Network": "", "InsurancePlan": "",
        "payer": "", "vip": "", "doctor": "", "Primary_Diagnosis": "",
        "secondery_diagnosis_codes": "", "Activity_Code": "", "Activity_Type": "",
        "Quantity": "", "current_date": "", "relation_id": "", "Activity_Start": "",
        "Observation": "", "Encounter": "", "gross": "", "patientShare": "",
        "net": "", "type": "", "drugs": "", "specialty": "", "total": ""
      }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Claims Template");
    
    const colWidths = [
      { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 8 }, { wch: 15 },
      { wch: 18 }, { wch: 20 }, { wch: 15 }, { wch: 20 }, { wch: 20 },
      { wch: 18 }, { wch: 12 }, { wch: 22 }, { wch: 15 }, { wch: 18 },
      { wch: 12 }, { wch: 10 }, { wch: 15 }, { wch: 20 }, { wch: 28 },
      { wch: 15 }, { wch: 15 }, { wch: 10 }, { wch: 18 }, { wch: 12 },
      { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 10 }, { wch: 15 },
      { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 15 }, { wch: 10 }
    ];
    ws['!cols'] = colWidths;
    
    XLSX.writeFile(wb, `Claims_Validation_Template_${new Date().getTime()}.xlsx`);
  };

  const handleBulkUpload = async () => {
    if (!uploadedFile) {
      toast({
        title: "Error",
        description: "Please select a file to upload",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    setUploadProgress(0);
    setErrorLogs([]);

    const formData = new FormData();
    formData.append('file', uploadedFile);

    let progressInterval: NodeJS.Timeout | undefined;

    try {
      progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            if (progressInterval) clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 500);

      const response = await ClaimValidationAxiosInstance.post(
        "validate-file",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "Authorization": `Bearer ${token}`
          }
        }
      );

      if (progressInterval) clearInterval(progressInterval);

      // تحقق من الـstatus codes
      if (response.status === 401) {
        setUploadProgress(0);
        setResponseMessage("Unauthorized: Invalid or expired token. Please login again.");
        toast({
          title: "Unauthorized",
          description: "Invalid or expired token. Please login again.",
          variant: "destructive"
        });
        return;
      }

      if (response.status === 400) {
        setUploadProgress(0);
        const errorMsg = response.data?.error || response.data?.message || "Invalid file format or data";
        setResponseMessage(`Bad Request: ${errorMsg}`);
        if (response.data?.parsingErrors) {
          setErrorLogs(response.data.parsingErrors);
        }
        toast({
          title: "Bad Request",
          description: errorMsg,
          variant: "destructive"
        });
        return;
      }

      setUploadProgress(100);

      // استخراج النتائج حسب الـSwagger structure
      console.log("API Response:", response.data);
      console.log("First Validation:", response.data?.validations?.[0]);
console.log("First Validation Details:", response.data?.validations?.[0]?.validations?.[0]);
      const validations = response.data?.validations || [];

      if (Array.isArray(validations) && validations.length > 0) {
        const totalClaims = validations.length;
        const failedClaims = validations.filter((v: ClaimResult) =>
          v.validations?.some((val: Validation) => {
            const result = typeof val.result === "string" 
              ? { success: false, message: val.result }
              : val.result || {};
            return !result.success && result.message !== "PASS";
          })
        ).length;

        const passedClaims = totalClaims - failedClaims;

        setBulkValidationResults(validations);
        
        // حفظ النتائج والرسالة في localStorage
        localStorage.setItem('bulkValidationResults', JSON.stringify(validations));
        
        const successMessage = `${totalClaims} claims processed successfully! (${passedClaims} passed, ${failedClaims} failed)`;
        setResponseMessage(successMessage);
        localStorage.setItem('responseMessage', successMessage);

        if (response.data.success !== false) {
          toast({
            title: "Success",
            description: `${totalClaims} claims processed! ${passedClaims} passed, ${failedClaims} failed`,
          });
        } else {
          toast({
            title: "Warning",
            description: `${totalClaims} claims processed with errors`,
            variant: "destructive"
          });
        }

        // حفظ أخطاء الـparsing إذا موجودة
        if (response.data.parsingErrors) {
          setErrorLogs(response.data.parsingErrors);
        }
      } else {
        setResponseMessage(response.data?.message || "No validation results returned");
        toast({
          title: "Error",
          description: "No validation results returned",
          variant: "destructive"
        });
      }

    } catch (error: any) {
      console.error("Bulk upload failed:", error);

      if (progressInterval) clearInterval(progressInterval);
      setUploadProgress(0);

      // محاولة استخراج النتائج من الـerror response
      console.log("Error Response:", error.response?.data);
      const validations = error.response?.data?.validations || [];

      if (Array.isArray(validations) && validations.length > 0) {
        const totalClaims = validations.length;
        const failedClaims = validations.filter((v: ClaimResult) =>
          v.validations?.some((val: Validation) => {
            const result = typeof val.result === "string"
              ? { success: false, message: val.result }
              : val.result || {};
            return !result.success && result.message !== "PASS";
          })
        ).length;

        setBulkValidationResults(validations);
        localStorage.setItem('bulkValidationResults', JSON.stringify(validations));
        setUploadProgress(100);

        const errorMessage = `${totalClaims} claims processed with errors! (${totalClaims - failedClaims} passed, ${failedClaims} failed)`;
        setResponseMessage(errorMessage);
        localStorage.setItem('responseMessage', errorMessage);

        if (error.response?.data?.parsingErrors) {
          setErrorLogs(error.response.data.parsingErrors);
        }

        toast({
          title: "Partial Success",
          description: `${totalClaims} claims processed with some errors`,
          variant: "destructive"
        });
      } else if (error.response?.status === 401) {
        setResponseMessage("Unauthorized: Invalid or expired token. Please login again.");
        toast({
          title: "Unauthorized",
          description: "Invalid or expired token",
          variant: "destructive"
        });
      } else if (error.response?.status === 400) {
        const errorMsg = error.response.data?.error || error.response.data?.message || "Invalid file format or data";
        setResponseMessage(`Bad Request: ${errorMsg}`);
        if (error.response.data?.parsingErrors) {
          setErrorLogs(error.response.data.parsingErrors);
        }
        toast({
          title: "Bad Request",
          description: errorMsg,
          variant: "destructive"
        });
      } else {
        const errorMessage = error.response?.data?.message || error.message || "Unknown error occurred";
        setResponseMessage(`Upload failed: ${errorMessage}`);

        if (error.response?.data?.parsingErrors) {
          setErrorLogs(error.response.data.parsingErrors);
        }

        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive"
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const exportResultsWithOriginalData = async () => {
    if (bulkValidationResults.length === 0) {
      toast({
        title: "No Results",
        description: "There are no bulk upload results to export.",
        variant: "destructive"
      });
      return;
    }

    try {
      toast({
        title: "Downloading...",
        description: "Please wait while we prepare your file",
      });

      // حسب الـSwagger، الـAPI بتاخذ data و token
      const response = await ClaimValidationAxiosInstance.post(
        "export-validation-excel",
        { 
          data: bulkValidationResults,
          token: token
        },
        {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Validation_Results_${new Date().getTime()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast({
        title: "Success",
        description: "Validation results exported successfully!",
      });

    } catch (error: any) {
      console.error("Export failed:", error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to export results",
        variant: "destructive"
      });
    }
  };

  // Filter results
  const filteredResults = bulkValidationResults.filter((result) => {
    const hasFailed = result.validations?.some((v) => {
      const res = typeof v.result === "string"
        ? { success: false, message: v.result }
        : v.result || {};
      return !res.success && res.message !== "PASS";
    });

    const matchesTab = 
      activeTab === 'all' ? true :
      activeTab === 'passed' ? !hasFailed :
      activeTab === 'failed' ? hasFailed : true;

    const claimIdStr = result.claim_ID ? String(result.claim_ID).toLowerCase() : '';
    const patientStr = result.patients ? String(result.patients).toLowerCase() : '';
    const searchStr = searchQuery.toLowerCase();
    
    const matchesSearch = 
      !searchQuery ||
      claimIdStr.includes(searchStr) ||
      patientStr.includes(searchStr);

    return matchesTab && matchesSearch;
  });

  // Pagination
  const totalPages = Math.ceil(filteredResults.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedResults = filteredResults.slice(startIndex, startIndex + itemsPerPage);

  const passedCount = bulkValidationResults.filter((r) => 
    !r.validations?.some((v) => {
      const result = typeof v.result === "string"
        ? { success: false, message: v.result }
        : v.result || {};
      return !result.success && result.message !== "PASS";
    })
  ).length;

  const failedCount = bulkValidationResults.length - passedCount;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 pt-24 px-8 pb-8">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Page Header */}


{/* Upload Section */}

        {/* Upload Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-4 py-3 border-b border-blue-200">
  <div className="flex items-center justify-between gap-3">
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
        <i className="fas fa-cloud-upload-alt text-white"></i>
      </div>
      <div>
        <h2 className="text-lg font-bold text-gray-800">Upload Claims File</h2>
        <p className="text-xs text-gray-600">Select your Excel, CSV, or JSON file (Max 10MB)</p>
      </div>
    </div>
    <Button
      onClick={downloadTemplate}
      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow-md hover:shadow-lg hover:scale-105 transition-all text-sm font-semibold"
    >
      <i className="fas fa-download mr-2"></i>
      Download Template
    </Button>
  </div>
</div>

          <div className="p-4">
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <label 
                  htmlFor="file-upload" 
                  className="block cursor-pointer group"
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.add('border-blue-500', 'bg-blue-50');
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
                    const file = e.dataTransfer.files?.[0];
                    if (file) {
                      const validTypes = ['.xlsx', '.xls', '.csv', '.json'];
                      const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();
                      if (validTypes.includes(fileExt)) {
                        setUploadedFile(file);
                        toast({
                          title: "File Selected",
                          description: `${file.name} is ready to upload`,
                        });
                      } else {
                        toast({
                          title: "Invalid File",
                          description: "Please select a valid file format",
                          variant: "destructive"
                        });
                      }
                    }
                  }}
                >
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 transition-all duration-200 group-hover:border-blue-500 group-hover:bg-blue-50/30">
                    <div className="flex items-center justify-center gap-6">
                      <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                        <i className="fas fa-cloud-upload-alt text-blue-600 text-2xl"></i>
                      </div>
                      <div className="flex-1 text-center">
                        <h3 className="text-lg font-semibold text-gray-800 mb-1">
                          {uploadedFile ? (
                            <span className="text-green-600 flex items-center justify-center gap-2">
                              <i className="fas fa-check-circle"></i>
                              {uploadedFile.name}
                            </span>
                          ) : (
                            "Choose file or drag & drop here"
                          )}
                        </h3>
                        <p className="text-sm text-gray-500 mb-3">
                          Supported formats: Excel (.xlsx, .xls), CSV (.csv), JSON (.json)
                        </p>
                        {!uploadedFile && (
                          <div className="inline-flex px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors">
                            <i className="fas fa-folder-open mr-2"></i>
                            Browse Files
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </label>
                
                <input
                  id="file-upload"
                  type="file"
                  accept=".xlsx,.xls,.csv,.json"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setUploadedFile(file);
                      toast({
                        title: "File Selected",
                        description: `${file.name} is ready to upload`,
                      });
                    }
                  }}
                />

                {uploadedFile && (
                  <div className="mt-4 bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                          <i className="fas fa-file-alt text-white"></i>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800">{uploadedFile.name}</p>
                          <p className="text-xs text-gray-600">{(uploadedFile.size / 1024).toFixed(2)} KB</p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setUploadedFile(null);
                          const fileInput = document.getElementById('file-upload') as HTMLInputElement;
                          if (fileInput) fileInput.value = '';
                        }}
                        className="w-8 h-8 bg-red-100 hover:bg-red-200 rounded-lg flex items-center justify-center text-red-600 transition-colors"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>

                    {uploadProgress > 0 && uploadProgress < 100 && (
                      <div className="mt-3">
                        <div className="flex justify-between mb-1">
                          <span className="text-xs font-semibold text-gray-700">
                            <i className="fas fa-spinner fa-spin mr-1"></i>
                            Processing claims...
                          </span>
                          <span className="text-xs font-bold text-blue-600">{uploadProgress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden shadow-inner">
                          <div 
                            className="bg-gradient-to-r from-blue-500 via-blue-600 to-purple-600 h-full rounded-full transition-all duration-300 relative"
                            style={{ width: `${uploadProgress}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500 mt-1 text-center">Please wait while we validate your claims...</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-3">
                <Button
                  onClick={handleBulkUpload}
                  disabled={!uploadedFile || isLoading}
                  className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-700 hover:to-green-800 text-white px-6 py-3 rounded-xl shadow-lg hover:scale-105 font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px]"
                >
                  {isLoading ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Processing...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-rocket mr-2"></i>
                      Validate
                    </>
                  )}
                </Button>

                <Button
                  onClick={handleResetBulkUpload}
                  disabled={isLoading}
                  className="bg-gradient-to-r from-gray-100 to-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-3 rounded-xl shadow-md hover:shadow-lg hover:scale-105 transition-all disabled:opacity-50 min-w-[140px]"
                >
                  <i className="fas fa-redo mr-2"></i>
                  Reset
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Status Message */}
        {responseMessage && (
          <div className={`rounded-xl p-4 flex items-center gap-3 shadow-sm ${
            responseMessage.includes("failed") || responseMessage.includes("Bad Request")
              ? "bg-red-50 border-2 border-red-200"
              : "bg-green-50 border-2 border-green-200"
          }`}>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
              responseMessage.includes("failed") || responseMessage.includes("Bad Request")
                ? "bg-red-100"
                : "bg-green-100"
            }`}>
              <i className={`fas ${
                responseMessage.includes("failed") || responseMessage.includes("Bad Request")
                  ? "fa-exclamation-circle text-red-600"
                  : "fa-check-circle text-green-600"
              } text-xl`}></i>
            </div>
            <p className={`flex-1 font-medium ${
              responseMessage.includes("failed") || responseMessage.includes("Bad Request")
                ? "text-red-800"
                : "text-green-800"
            }`}>
              {responseMessage}
            </p>
          </div>
        )}

{errorLogs.length > 0 && (
  <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4">
    <div className="flex items-center gap-3 mb-3">
      <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
        <i className="fas fa-exclamation-triangle text-yellow-600 text-xl"></i>
      </div>
      <div>
        <h3 className="font-bold text-yellow-800">Parsing Errors</h3>
        <p className="text-sm text-yellow-600">Some rows in your file had issues</p>
      </div>
    </div>
    <ul className="space-y-1">
      {errorLogs.map((error, idx) => (
        <li key={idx} className="text-sm text-yellow-700 flex items-start gap-2">
          <i className="fas fa-circle text-yellow-400 text-xs mt-1.5"></i>
          <span>{error}</span>
        </li>
      ))}
    </ul>
  </div>
)}
        {/* Results Section */}
        {bulkValidationResults.length > 0 && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="bg-white rounded-md border border-gray-200 p-3 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[11px] font-medium text-gray-500">Total Claims</p>
                    <p className="text-xl font-semibold text-gray-800">{bulkValidationResults.length}</p>
                  </div>
                  <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
                    <i className="fas fa-file-medical text-blue-600 text-sm"></i>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-md border border-green-200 p-3 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[11px] font-medium text-gray-500">Passed</p>
                    <p className="text-xl font-semibold text-green-600">{passedCount}</p>
                    <p className="text-[10px] text-gray-400">
                      {bulkValidationResults.length
                        ? `${((passedCount / bulkValidationResults.length) * 100).toFixed(1)}%`
                        : '0%'}
                    </p>
                  </div>
                  <div className="w-8 h-8 bg-green-100 rounded-md flex items-center justify-center">
                    <i className="fas fa-check text-green-600 text-sm"></i>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-md border border-red-200 p-3 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[11px] font-medium text-gray-500">Failed</p>
                    <p className="text-xl font-semibold text-red-600">{failedCount}</p>
                    <p className="text-[10px] text-gray-400">
                      {bulkValidationResults.length
                        ? `${((failedCount / bulkValidationResults.length) * 100).toFixed(1)}%`
                        : '0%'}
                    </p>
                  </div>
                  <div className="w-8 h-8 bg-red-100 rounded-md flex items-center justify-center">
                    <i className="fas fa-times text-red-600 text-sm"></i>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-md border border-purple-200 p-3 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[11px] font-medium text-gray-500">Success Rate</p>
                    <p className="text-xl font-semibold text-purple-600">
                      {bulkValidationResults.length
                        ? `${((passedCount / bulkValidationResults.length) * 100).toFixed(0)}%`
                        : '0%'}
                    </p>
                  </div>
                  <div className="w-8 h-8 bg-purple-100 rounded-md flex items-center justify-center">
                    <i className="fas fa-chart-line text-purple-600 text-sm"></i>
                  </div>
                </div>
              </div>
            </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
        <i className="fas fa-file-excel text-white"></i>
      </div>
      <div>
        <h3 className="font-bold text-gray-800">Export Results</h3>
        <p className="text-xs text-gray-600">Download validation results with all claim data</p>
      </div>
    </div>
    <div className="flex items-center gap-3">
     
      <Button
        onClick={exportResultsWithOriginalData}
        className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-4 py-2 rounded-lg shadow-md hover:shadow-lg hover:scale-105 transition-all text-sm"
      >
        <i className="fas fa-download mr-2"></i>
        Download Response
      </Button>
    </div>
  </div>
</div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => { setActiveTab('all'); setCurrentPage(1); }}
                    className={`px-5 py-2.5 rounded-lg font-semibold transition-all ${
                      activeTab === 'all'
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <i className="fas fa-list mr-2"></i>
                    All ({bulkValidationResults.length})
                  </button>
                  <button
                    onClick={() => { setActiveTab('passed'); setCurrentPage(1); }}
                    className={`px-5 py-2.5 rounded-lg font-semibold transition-all ${
                      activeTab === 'passed'
                        ? 'bg-green-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <i className="fas fa-check mr-2"></i>
                    Passed ({passedCount})
                  </button>
                  <button
                    onClick={() => { setActiveTab('failed'); setCurrentPage(1); }}
                    className={`px-5 py-2.5 rounded-lg font-semibold transition-all ${
                      activeTab === 'failed'
                        ? 'bg-red-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <i className="fas fa-times mr-2"></i>
                    Failed ({failedCount})
                  </button>
                </div>

                <div className="relative flex-1 md:flex-initial">
                  <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                  <input
                    type="text"
                    placeholder="Search claims..."
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                    className="pl-10 pr-10 py-2.5 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full md:w-64"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => { setSearchQuery(''); setCurrentPage(1); }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-4 max-h-[800px] overflow-y-auto pr-2">
              {paginatedResults.length > 0 ? (
                paginatedResults.map((resultGroup, groupIndex) => {
                  const hasFailed = resultGroup.validations?.some((v) => {
                    const result = typeof v.result === "string"
                      ? { success: false, message: v.result }
                      : v.result || {};
                    return !result.success && result.message !== "PASS";
                  });

                  const actualIndex = startIndex + groupIndex;
                  
                  return (
                    <div 
                      key={actualIndex} 
                      className={`bg-white rounded-xl shadow-md border-2 transition-all hover:shadow-lg ${
                        hasFailed 
                          ? 'border-red-200 hover:border-red-300' 
                          : 'border-green-200 hover:border-green-300'
                      }`}
                    >
                      <div className={`px-6 py-4 flex items-center justify-between border-b-2 ${
                        hasFailed 
                          ? 'bg-gradient-to-r from-blue-50 to-slate-100 border-blue-200' 
                          : 'bg-gradient-to-r from-blue-50 to-slate-100 border-blue-200'
                      }`}>
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg shadow-sm ${
                            hasFailed 
                              ? 'bg-blue-600 text-white' 
                              : 'bg-blue-600 text-white'
                          }`}>
                            #{actualIndex + 1}
                          </div>
                          <div>
                            {resultGroup.claim_ID && (
                              <h3 className="font-bold text-lg text-gray-800 flex items-center gap-2">
                                <i className="fas fa-file-invoice text-blue-600"></i>
                                Claim: {resultGroup.claim_ID}
                              </h3>
                            )}
                            {resultGroup.patients && (
                              <p className="text-sm text-gray-600 flex items-center gap-2 mt-1">
                                <i className="fas fa-user text-gray-400"></i>
                                Patient: {resultGroup.patients}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div className={`px-6 py-2 rounded-full font-bold shadow-sm ${
                          hasFailed 
                            ? 'bg-red-500 text-white' 
                            : 'bg-green-500 text-white'
                        }`}>
                          {hasFailed ? (
                            <><i className="fas fa-times-circle mr-2"></i>FAILED</>
                          ) : (
                            <><i className="fas fa-check-circle mr-2"></i>PASSED</>
                          )}
                        </div>
                      </div>

                      <div className="p-4">
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="bg-gradient-to-r from-blue-50 to-blue-100 border-b-2 border-blue-200">
                                <th className="text-left py-3 px-4 font-bold text-blue-900 text-sm">Validation</th>
                                <th className="text-center py-3 px-4 font-bold text-blue-900 text-sm">Status</th>
<th className="text-left py-3 px-4 font-bold text-blue-900 text-sm">Benefit</th>
<th className="text-left py-3 px-4 font-bold text-blue-900 text-sm">Sub Benefit</th>
                                <th className="text-left py-3 px-4 font-bold text-blue-900 text-sm">Code</th>
                                <th className="text-left py-3 px-4 font-bold text-blue-900 text-sm">Type</th>
                                <th className="text-left py-3 px-4 font-bold text-blue-900 text-sm">Details</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(resultGroup.validations || []).flatMap(
                                (validation: Validation, idx: number) => {
                                  const result = typeof validation.result === "string"
                                    ? { success: false, message: validation.result }
                                    : validation.result || {};

                                  const validationType = validation.type || result.relation_Type || result.error_type || result.type || "N/A";
                                  const validationCode = Array.isArray(validation.code)
                                    ? validation.code.join(", ")
                                    : validation.code || result.activityCode || result.code || "N/A";
                                const mainBenefit = validation.benefit || result.benefit || "N/A";
const subBenefit = validation.usedBenefit || result.usedBenefit || "N/A";
                                  const isPassed = result.message === "PASS" || result.success;

                                  const rows: JSX.Element[] = [];

                                  if (validation.name === "SayanaValidation" && result.isParent && result.children && result.children.length > 0) {
                                    const rowKey = `bulk-${resultGroup.claim_ID || 'unknown'}-${validation.code}-${idx}`;
                                    const isExpanded = expandedRows.has(rowKey);

                                    rows.push(
                                      <tr key={`${idx}-parent`} className={`border-b border-gray-200 hover:bg-blue-50 transition-colors ${isExpanded ? 'bg-blue-50' : ''}`}>
                                        <td className="py-3 px-4">
                                          <button
                                            onClick={() => toggleRowExpansion(rowKey)}
                                            className="flex items-center gap-2 text-left w-full group"
                                          >
                                            <span className="bg-blue-600 text-white w-6 h-6 rounded-lg flex items-center justify-center text-sm font-bold group-hover:bg-blue-700 transition-colors shadow-sm">
                                              {isExpanded ? "−" : "+"}
                                            </span>
                                            <span className="font-semibold text-gray-800">{validation.name}</span>
                                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-md">
                                              {result.errorType || 'Activity'}
                                            </span>
                                          </button>
                                        </td>
                                        <td className="py-3 px-4 text-center">
                                          <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-bold shadow-sm ${
                                            isPassed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                          }`}>
                                            <i className={`fas ${isPassed ? 'fa-check' : 'fa-times'}`}></i>
                                            {isPassed ? "PASS" : "FAIL"}
                                          </span>
                                        </td>
                                      <td className="py-3 px-4">
  {mainBenefit !== "N/A" ? (
    <span className="text-blue-700 font-medium bg-blue-50 px-2 py-1 rounded">{mainBenefit}</span>
  ) : (
    <span className="text-gray-400">—</span>
  )}
</td>
<td className="py-3 px-4">
  {subBenefit !== "N/A" ? (
    <span className="text-purple-700 font-medium bg-purple-50 px-2 py-1 rounded">{subBenefit}</span>
  ) : (
    <span className="text-gray-400">—</span>
  )}
</td>
                                        <td className="py-3 px-4 font-mono text-sm text-gray-700 bg-gray-50 rounded">{validationCode}</td>
                                        <td className="py-3 px-4">
                                          <span className="bg-blue-100 text-blue-800 px-3 py-1.5 rounded-lg text-sm font-semibold inline-block min-w-[80px] text-center">
                                            {validationType}
                                          </span>
                                        </td>
                                        <td className="py-3 px-4 text-sm text-gray-600">
                                          {typeof validation.result === "string"
                                            ? validation.result
                                            : result.message || result.error || "No details"}
                                        </td>
                                      </tr>
                                    );

                                    if (isExpanded) {
                                      result.children.forEach((child: any, childIdx: number) => {
                                        const childCode = child.isActivityError ? (child.activityCode || "N/A") : (child.diagnosisCode || "N/A");
                                        const childPassed = child.message === "PASS";

                                        rows.push(
                                         <tr key={`${idx}-child-${childIdx}`} className="bg-blue-50/50 border-b border-blue-100">
  <td className="py-2 px-4 pl-12">
    <span className="text-gray-600 text-sm flex items-center gap-2">
      <i className="fas fa-level-down-alt text-blue-400"></i>
      <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-medium">
        {child.isActivityError ? "Activity" : "Diagnosis"} Child
      </span>
    </span>
  </td>
  <td className="py-2 px-4 text-center">
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${
      childPassed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
    }`}>
      <i className={`fas ${childPassed ? 'fa-check' : 'fa-times'}`}></i>
    </span>
  </td>
  <td className="py-2 px-4 text-gray-400 text-sm">—</td>
  <td className="py-2 px-4 text-gray-400 text-sm">—</td> 
  <td className="py-2 px-4 font-mono text-xs text-gray-600">{childCode}</td>
  <td className="py-2 px-4">
    <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">
      {child.relationType || child.errorType || "N/A"}
    </span>
  </td>
  <td className="py-2 px-4 text-xs text-gray-600">{child.message || "No details"}</td>
</tr>
                                        );
                                      });
                                    }
                                  } else {
                                    rows.push(
                                     <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
  <td className="py-3 px-4 font-semibold text-gray-800">{validation.name}</td>
  <td className="py-3 px-4 text-center">
    <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-bold shadow-sm ${
      isPassed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
    }`}>
      <i className={`fas ${isPassed ? 'fa-check' : 'fa-times'}`}></i>
      {isPassed ? "PASS" : "FAIL"}
    </span>
  </td>
  <td className="py-3 px-4">
    {mainBenefit !== "N/A" ? (
      <span className="text-blue-700 font-medium bg-blue-50 px-2 py-1 rounded">{mainBenefit}</span>
    ) : (
      <span className="text-gray-400">—</span>
    )}
  </td>
  <td className="py-3 px-4">
    {subBenefit !== "N/A" ? (
      <span className="text-purple-700 font-medium bg-purple-50 px-2 py-1 rounded">{subBenefit}</span>
    ) : (
      <span className="text-gray-400">—</span>
    )}
  </td>
  <td className="py-3 px-4 font-mono text-sm text-gray-700">{validationCode}</td>
  <td className="py-3 px-4">
    <span className="bg-blue-100 text-blue-800 px-3 py-1.5 rounded-lg text-sm font-semibold inline-block min-w-[80px] text-center">
      {validationType}
    </span>
  </td>
  <td className="py-3 px-4 text-sm text-gray-600">
    {typeof validation.result === "string"
      ? validation.result
      : result.message || result.error || "No details"}
  </td>
</tr>
                                    );
                                  }

                                  return rows;
                                }
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-12 text-center">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="fas fa-inbox text-4xl text-gray-300"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">No Claims Found</h3>
                  <p className="text-gray-600">No claims match your current filter criteria</p>
                </div>
              )}
            </div>

            {totalPages > 1 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <p className="text-sm text-gray-600 font-medium">
                    Showing <span className="font-bold text-gray-800">{startIndex + 1}</span> to <span className="font-bold text-gray-800">{Math.min(startIndex + itemsPerPage, filteredResults.length)}</span> of <span className="font-bold text-gray-800">{filteredResults.length}</span> results
                  </p>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 rounded-lg border-2 border-gray-300 font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      <i className="fas fa-chevron-left"></i>
                    </button>
                    
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                            currentPage === pageNum
                              ? 'bg-blue-600 text-white shadow-md'
                              : 'border-2 border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 rounded-lg border-2 border-gray-300 font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkClaimValidation;